import { Button, buttonVariants, cn, Icon } from '@atlas/ui-primitives'
import { ChevronLeft, ChevronRight } from 'lucide-react'

/** A slot in the page strip: a page number, or a run of skipped pages. */
export type PageSlot = number | 'ellipsis'

/**
 * The pages to show: the first and last, the current one and one either side.
 * A gap of a single page shows that page, since an ellipsis would hide no more
 * than the number it replaces.
 */
export function pageWindow(page: number, totalPages: number): PageSlot[] {
	const shown = [1, page - 1, page, page + 1, totalPages].filter((n) => n >= 1 && n <= totalPages)
	const pages = [...new Set(shown)].sort((a, b) => a - b)
	const slots: PageSlot[] = []
	for (const n of pages) {
		const previous = slots[slots.length - 1]
		if (typeof previous === 'number' && n - previous === 2) slots.push(previous + 1)
		else if (typeof previous === 'number' && n - previous > 2) slots.push('ellipsis')
		slots.push(n)
	}
	return slots
}

export interface NumberedPaginationProps {
	/** 1-based current page. */
	page: number
	/** Total pages, as reported by the endpoint. */
	totalPages: number
	/** Disables every control while a page is in flight, so a fast clicker cannot outrun the fetch. */
	isFetching?: boolean
	onPage: (next: number) => void
	className?: string
}

/**
 * Previous / next plus numbered pages. Page state stays with the caller; renders
 * nothing when there is only one page.
 */
export function NumberedPagination({
	page,
	totalPages,
	isFetching = false,
	onPage,
	className,
}: NumberedPaginationProps) {
	if (totalPages <= 1) return null
	const slots = pageWindow(page, totalPages)
	return (
		<nav aria-label="Pagination" className={cn('flex items-center justify-center gap-ds-1', className)}>
			<Button
				variant="ghost"
				size="sm"
				aria-label="Previous page"
				isDisabled={page <= 1 || isFetching}
				onPress={() => onPage(page - 1)}
			>
				<Icon size="sm">
					<ChevronLeft />
				</Icon>
			</Button>
			{slots.map((slot, index) =>
				slot === 'ellipsis' ? (
					<span key={`after-${slots[index - 1]}`} aria-hidden="true" className="px-ds-2 text-ds-sm text-ds-text-muted">
						…
					</span>
				) : slot === page ? (
					<span
						key={slot}
						aria-current="page"
						className={cn(buttonVariants({ variant: 'primary', size: 'sm' }), 'cursor-default')}
					>
						{slot}
					</span>
				) : (
					<Button
						key={slot}
						variant="ghost"
						size="sm"
						aria-label={`Page ${slot}`}
						isDisabled={isFetching}
						onPress={() => onPage(slot)}
					>
						{slot}
					</Button>
				),
			)}
			<Button
				variant="ghost"
				size="sm"
				aria-label="Next page"
				isDisabled={page >= totalPages || isFetching}
				onPress={() => onPage(page + 1)}
			>
				<Icon size="sm">
					<ChevronRight />
				</Icon>
			</Button>
		</nav>
	)
}
